import { Center, HStack, Switch } from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { byBoolean, byDate, byNumber, byString, byValues } from "sort-es";

import CompactNumber from "@/common/components/CompactNumber";
import DataCell from "@/common/components/DataCell";
import { DataTable } from "@/common/components/DataTable/DataTable";
import { DelayedSpinner } from "@/common/components/DelayedSpinner";
import InfoIconTooltip from "@/common/components/InfoIconTooltip";
import NotAvailableCell from "@/common/components/NotAvailableCell";
import TimestampCell from "@/common/components/TimestampCell";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useSafeDataTableStateProps } from "@/common/hooks/useDataTableParamState";
import { useTableDisplayModeParam } from "@/common/hooks/useTableDisplayMode";
import { createSortableColumnHelper } from "@/common/types/reactTableHelpers";
import {
  relativeTimeBucketLabel,
  relativeTimeBuckets,
} from "@/common/utils/datetime";
import { linkClickModifierHandler } from "@/common/utils/linkClickHandler";
import { getItemTree } from "@/common/utils/listItemTree";
import { CompanyFlagsEmptyState } from "@/company/components/CompanyFeaturesEmptyState";
import { CompanyFlagToggleDialog } from "@/company/components/CompanyFeatureToggleDialog";
import { useCompanyData } from "@/company/data/useCompanyData";
import {
  CompanyFlagsColumn,
  defaultCompanyFlagsColumns,
  groupingCompanyFlagsColumns,
  useCompanyFlagsTableState,
} from "@/company/hooks/useCompanyTableStates";
import { FeatureNameCell } from "@/feature/components/FeatureNameCell";
import { TableDisplayModePicker } from "@/feature/components/TableDisplayModePicker";
import { useFeatureNamesData } from "@/feature/data/useFeatureNamesData";
import { FeatureName } from "@reflag/shared/featureAPI";
import { EntityFlagDTO, EntityFlagsResponse } from "@reflag/shared/flagAPI";
import { FeatureUrl } from "@reflag/shared/urls";
import {
  ColumnSorter,
  nullableSort,
  SortClauses,
} from "@reflag/shared/utils/sorting";

type CompanyFlagsTableProps = {
  data?: EntityFlagsResponse;
  isLoading?: boolean;
  isFetching?: boolean;
  isError?: boolean;
  onRetry?: () => void;
};

type CompanyFlagTableRow = Omit<EntityFlagDTO, "id"> &
  Pick<
    FeatureName,
    | "icon"
    | "iconColor"
    | "source"
    | "isArchived"
    | "isPermanent"
    | "isStale"
    | "parentFeatureId"
  > & {
    id: string;
    featureId: string | null;
    flagId: string;
    subRows?: CompanyFlagTableRow[];
  };

export function CompanyFlagsTable({
  data,
  isLoading,
  isFetching,
  isError,
  onRetry,
}: CompanyFlagsTableProps) {
  const { companyId } = useParams();
  const { data: company } = useCompanyData(companyId);
  const { data: featureNames = [], isLoading: isFeatureNamesLoading } =
    useFeatureNamesData();

  const [toggleFlag, setToggleFlag] = useState<Pick<
    EntityFlagDTO,
    "key" | "name" | "specificTargetValue"
  > | null>(null);

  const { env } = useEnvironment();
  const navigate = useNavigate();

  const tableState = useCompanyFlagsTableState();
  // We don't want to pass along invalid props to the data table
  const safeTableProps = useSafeDataTableStateProps(tableState);

  const columnHelper = createSortableColumnHelper<
    CompanyFlagTableRow,
    CompanyFlagsColumn
  >();

  // Get display mode and grouping column
  const [displayMode] = useTableDisplayModeParam();

  const featureByFlagKey = useMemo(
    () => new Map(featureNames.map((feature) => [feature.key, feature])),
    [featureNames],
  );

  const rows = useMemo(() => {
    const flags = data?.data ?? [];
    return flags.map((flag) => {
      const feature = featureByFlagKey.get(flag.key);
      return {
        ...flag,
        id: feature?.id ?? flag.id,
        featureId: feature?.id ?? null,
        flagId: flag.id,
        icon: feature?.icon ?? null,
        iconColor: feature?.iconColor ?? null,
        source: feature?.source ?? "event",
        isArchived: feature?.isArchived ?? false,
        isPermanent: feature?.isPermanent ?? false,
        isStale: feature?.isStale ?? false,
        parentFeatureId: feature?.parentFeatureId ?? null,
      } satisfies CompanyFlagTableRow;
    });
  }, [data?.data, featureByFlagKey]);

  const columns = useMemo(() => {
    return [
      columnHelper.accessor("name", {
        header: "Flag",
        cell: (cell) => {
          if (cell.row.original.featureId === null) {
            return (
              <DataCell fontSize="sm" type="string" value={cell.getValue()} />
            );
          }
          return <FeatureNameCell {...cell} />;
        },
      }),
      columnHelper.accessor("key", {
        header: "Key",
        cell: (cell) => {
          const value = cell.getValue();
          return (
            <DataCell
              fontFamily="code"
              fontSize="sm"
              type="string"
              value={value}
            />
          );
        },
        enableSorting: false,
      }),
      columnHelper.accessor("value", {
        header: () => (
          <HStack>
            <span>Access</span>
          </HStack>
        ),
        cell: (cell) => {
          const { getValue, row } = cell;
          const isTargeted = getValue();

          if (row.getIsGrouped()) {
            return isTargeted ? "Enabled" : "Disabled";
          }

          const isSpecificallyTargeted =
            row.original.specificTargetValue === true;

          return (
            <HStack
              spacing={1}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <Switch
                isChecked={isSpecificallyTargeted || isTargeted}
                isDisabled={(!isSpecificallyTargeted && isTargeted) || !company}
                size="sm"
                onChange={() => {
                  setToggleFlag({
                    key: row.original.key,
                    name: row.original.name,
                    specificTargetValue: row.original.specificTargetValue,
                  });
                }}
              />
              {!isSpecificallyTargeted && isTargeted && (
                <InfoIconTooltip text="This flag is implicitly enabled by the access rules." />
              )}
            </HStack>
          );
        },
      }),
      columnHelper.accessor("firstExposureAt", {
        header: () => (
          <HStack>
            <span>First exposure</span>
            <InfoIconTooltip text="First time the company was exposed to this flag." />
          </HStack>
        ),
        cell: (cell) => {
          const value = cell.getValue();
          if (!value) return <NotAvailableCell />;
          return <TimestampCell color="dimmed" value={value} />;
        },
      }),
      columnHelper.accessor("lastExposureAt", {
        header: () => (
          <HStack>
            <span>Last exposure</span>
            <InfoIconTooltip text="Last time the company was exposed to this flag." />
          </HStack>
        ),
        cell: ({ row }) => {
          const { lastExposureAt } = row.original;
          if (!lastExposureAt) return <NotAvailableCell />;
          return <TimestampCell color="dimmed" value={lastExposureAt} />;
        },
      }),
      columnHelper.accessor("createdAt", {
        header: "Created",
        cell: (cell) => {
          const value = cell.getValue();
          if (cell.row.getIsGrouped()) {
            if (typeof value === "number") {
              return relativeTimeBucketLabel(value);
            }
            return relativeTimeBuckets(value);
          }
          return <TimestampCell value={value} />;
        },
        getGroupingValue: (row) => relativeTimeBuckets(row.createdAt, true),
      }),
      columnHelper.accessor("firstTrackAt", {
        header: "First track",
        cell: (cell) => {
          const value = cell.getValue();
          return value === null ? (
            <NotAvailableCell />
          ) : (
            <TimestampCell value={value} leftAlign />
          );
        },
      }),
      columnHelper.accessor("lastTrackAt", {
        header: "Last track",
        cell: (cell) => {
          const value = cell.getValue();
          return value === null ? (
            <NotAvailableCell />
          ) : (
            <TimestampCell value={value} leftAlign />
          );
        },
      }),
      columnHelper.accessor("exposureCount", {
        header: "Exposure count",
        cell: ({ row }) => {
          const { exposureCount } = row.original;
          return exposureCount == null ? (
            <NotAvailableCell />
          ) : (
            <CompactNumber fontSize="sm" value={exposureCount} />
          );
        },
      }),
      columnHelper.accessor("trackCount", {
        header: "Track count",
        cell: ({ row }) => {
          const { trackCount } = row.original;
          return trackCount == null ? (
            <NotAvailableCell />
          ) : (
            <CompactNumber value={trackCount} />
          );
        },
      }),
    ];
  }, [columnHelper, company]);

  const filteredFlags = useMemo(() => {
    if (!tableState.searchQuery) {
      return rows;
    }
    const query = tableState.searchQuery.toLowerCase();
    return rows.filter(
      (f) =>
        f.id === tableState.searchQuery ||
        f.featureId === tableState.searchQuery ||
        f.flagId === tableState.searchQuery ||
        f.key.toLowerCase().includes(query) ||
        f.name.toLowerCase().includes(query),
    );
  }, [rows, tableState.searchQuery]);

  const rowSortClauses = useMemo((): SortClauses<CompanyFlagTableRow> => {
    const [sort] = tableState.sorting;
    if (!sort) {
      return [];
    }
    return sortByCompanyFlagRowColumn(sort.id, sort.desc === true);
  }, [tableState.sorting]);

  const tableData = useMemo(() => {
    if (displayMode === "tree") {
      const tree = getItemTree(filteredFlags, "parentFeatureId");
      if (!rowSortClauses.length) {
        return tree;
      }
      return sortCompanyFlagTreeRows(tree, rowSortClauses);
    }

    if (!rowSortClauses.length) {
      return filteredFlags;
    }

    return [...filteredFlags].sort(byValues(rowSortClauses));
  }, [displayMode, filteredFlags, rowSortClauses]);

  if (isFeatureNamesLoading) {
    return (
      <Center flexGrow={1}>
        <DelayedSpinner />
      </Center>
    );
  }

  return (
    <>
      <DataTable
        columns={columns}
        data={tableData}
        defaultColumns={defaultCompanyFlagsColumns}
        displayMode={displayMode}
        emptyState={
          <CompanyFlagsEmptyState
            isError={isError}
            isLoading={isLoading}
            onRetry={onRetry}
          />
        }
        groupingSorter={sortByCompanyFlagGroupColumn}
        isFetching={isFetching}
        meta={{ sortType: displayMode === "tree" ? "hierarchical" : "flat" }}
        searchPlaceholder="Search by name, key, or ID"
        tableId="company-features"
        toolbarRightActions={() => (
          <TableDisplayModePicker<CompanyFlagsColumn>
            groupingColumn={tableState.groupingColumn}
            groupingColumns={groupingCompanyFlagsColumns}
            onGroupingColumnChange={tableState.onGroupingColumnChange}
          />
        )}
        variant="clickable"
        canCustomize
        canSearch
        canSort
        onRowClick={(row, e) => {
          if (row.original.featureId === null) {
            return;
          }
          const url = FeatureUrl(env, {
            id: row.original.featureId,
            name: row.original.name,
          });
          e.preventDefault();

          linkClickModifierHandler(url, () => {
            navigate(url);
          })(e);
        }}
        {...safeTableProps}
      />

      {toggleFlag && company && (
        <CompanyFlagToggleDialog
          key={toggleFlag.key}
          company={company}
          flag={toggleFlag}
          isOpen={true}
          onClose={() => setToggleFlag(null)}
        />
      )}
    </>
  );
}

const sortByCompanyFlagGroupColumn: ColumnSorter<
  CompanyFlagTableRow,
  CompanyFlagsColumn
> = (sortBy, desc) => {
  switch (sortBy) {
    case "value":
      return [[(i) => i.value, byBoolean({ desc: !desc })]];
    case "createdAt":
      return [
        [(i) => relativeTimeBuckets(i.createdAt, true), byNumber({ desc })],
      ];
    default:
      return [];
  }
};

const sortByCompanyFlagRowColumn: ColumnSorter<
  CompanyFlagTableRow,
  CompanyFlagsColumn
> = (sortBy, desc) => {
  switch (sortBy) {
    case "name":
    case "key":
      return [[(i) => i[sortBy], byString({ lowercase: true, desc })]];
    case "value":
      return [[(i) => i.value, byBoolean({ desc: !desc })]];
    case "createdAt":
      return [[(i) => i.createdAt, byDate({ desc })]];
    case "firstExposureAt":
    case "lastExposureAt":
    case "lastCheckAt":
    case "firstTrackAt":
    case "lastTrackAt":
      return [
        [(i) => (i[sortBy] ? new Date(i[sortBy]) : null), nullableSort(desc)],
      ];
    case "exposureCount":
    case "trackCount":
      return [[(i) => i[sortBy], byNumber({ desc })]];
    default:
      return [];
  }
};

function sortCompanyFlagTreeRows(
  rows: CompanyFlagTableRow[],
  sortClauses: SortClauses<CompanyFlagTableRow>,
): CompanyFlagTableRow[] {
  return [...rows]
    .map((row) => ({
      ...row,
      subRows: row.subRows
        ? sortCompanyFlagTreeRows(row.subRows, sortClauses)
        : row.subRows,
    }))
    .sort(byValues(sortClauses));
}
